import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { createProcedureRevisionStatus } from "../../../../redux/procedureRevisionStatusSlice";
import { getRevAllNodes } from "../../../../redux/procedureRevisionSlice";
import { extractVersionStructure, getFieldsIncluded } from "../../../../util/getAuditTrailData";
import { generateSHA256 } from "../../../../util/generateSHA256";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import Snackbar from "../../../../common/Snackbar";
import SignatureField from "../../../../common/FormFields/SignatureField";
import { ModalActions } from "../../../../common/Buttons";

const RejectRevisionModal = ({
  isOpen,
  onClose,
  partId,
  partNumber,
  type,
  description,
  myRevision,
  revisionId,
  revisionData,
  submissionBy,
  onSuccess,
}) => {
  const [revisionDetails, setRevisionDetails] = useState("");
  const [password, setPassword] = useState("");
  const [signature, setSignature] = useState("");
  const [isReviewed, setIsReviewed] = useState(false);
  const [isRejectDisabled, setIsRejectDisabled] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const sigPadRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get signature auth methods from localStorage
  const [authMethods, setAuthMethods] = useState([]);
  useEffect(() => {
    try {
      const storedMethods = localStorage.getItem("signature_auth_methods");
      if (storedMethods) {
        const methods = JSON.parse(storedMethods);
        setAuthMethods(Array.isArray(methods) ? methods : []);
      } else {
        const userInfo = localStorage.getItem("loggedinUserInfo");
        if (userInfo) {
          try {
            const parsed = JSON.parse(userInfo);
            if (
              parsed.signature_auth_methods &&
              Array.isArray(parsed.signature_auth_methods)
            ) {
              setAuthMethods(parsed.signature_auth_methods);
            } else {
              setAuthMethods(["password", "signature"]);
            }
          } catch (e) {
            setAuthMethods(["password", "signature"]);
          }
        } else {
          setAuthMethods(["password", "signature"]);
        }
      }
    } catch (error) {
      console.error("Error parsing signature_auth_methods:", error);
      setAuthMethods(["password", "signature"]);
    }
  }, []);

  const [auditData] = useState({
    user_ref: localStorage.getItem("user_id") || "",
    ip_address: "",
    device_info: navigator.userAgent,
    hash: generateSHA256(Math.random().toString()),
    signature: "",
  });

  const handleEndDrawing = () => {
    if (sigPadRef.current?.getCanvas()) {
      const dataUrl = sigPadRef.current.getCanvas().toDataURL("image/png");
      setSignature(dataUrl);
    }
  };

  const handleClearSignature = () => {
    setSignature("");
  };

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", type: "" });
  };

  const handleRejectRevision = async () => {
    if (!revisionDetails) {
      showSnackbar("Comment is mandatory.", "error");
      return;
    }
    // Only validate password if it's in auth methods
    if (authMethods.includes("password") && !password) {
      showSnackbar("Password is mandatory.", "error");
      return;
    }
    // Only validate signature if it's in auth methods
    if (authMethods.includes("signature")) {
      const isSignatureEmpty = sigPadRef.current?.isEmpty() ?? true;
      if (!signature || isSignatureEmpty) {
        showSnackbar("Signature is mandatory.", "error");
        return;
      }
    }
    if (!isReviewed) {
      showSnackbar(
        "Please select the checkbox of review the revisions before rejecting.",
        "error"
      );
      return;
    }

    setIsRejectDisabled(true);

    const prevData = revisionData.find(
      (r) => r.procedure_revision_id === revisionId
    );

    // Fetch all revision nodes data
    let revisionNodes = [];
    try {
      const nodesResult = await dispatch(
        getRevAllNodes({ partId, revisionId })
      ).unwrap();
      
      if (nodesResult) {
        // Extract version content using extractVersionStructure
        // revisionNodes = extractVersionStructure(nodesResult) || [];
        revisionNodes = nodesResult || [];
      }
    } catch (error) {
      console.error("Error fetching revision nodes:", error);
      // Continue with empty array if fetch fails
    }

    const formData = {
      procedure_revision_id_ref: revisionId,
      status_changed_to: "Rejected",
      is_in_release_history: true,
      user_ref: submissionBy,
      is_open: true,
      purpose: "Reject",
      is_latest: true,
      past_procedure_revisions_reviewed: "{}",
      audit_trail_data: {
        signature: authMethods.includes("signature") ? signature : "",
        ...(authMethods.includes("password") && password
          ? { hash: password }
          : {}),
        ...(!authMethods.includes("password")
          ? {
              is_password_required: false,
            }
          : {
              is_password_required: true,
            }),
        user_ref: localStorage.getItem("user_id"),
        action: "REVISION_REJECTED",
        comment: revisionDetails,
        parent_version: prevData?.version_id_ref,
        version_content: {
          ...prevData,
          rejector: localStorage.user_name,
          action_to_show: `Rejected ${partNumber} Rev ${myRevision}`,
          procedure_number: partNumber,
          type: type,
          description: description,
          item_code_type: type,
          partNumber: partNumber,
          status: "Rejected",
          revisionNodes: revisionNodes,
          ...getFieldsIncluded()
        },
        password,
      },
    };

    try {
      await dispatch(createProcedureRevisionStatus(formData)).unwrap();
      showSnackbar("Revision rejected successfully!", "success");
      onClose();

      navigate(
        `/rejected-revision/${partId}/${myRevision}/${type}/${partNumber}/no-description/Rejected/${revisionId}`
      );

      setSnackbar({ open: false, message: "", type: "" });
      setIsRejectDisabled(false);
      setRevisionDetails("");
      setPassword("");
      setSignature("");
      setIsReviewed(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const errorMessage = error?.message || error || "Unknown error";
      // For "Incorrect password", show the message directly
      if (errorMessage === "Incorrect password") {
        showSnackbar("Incorrect password", "error");
      } else {
        showSnackbar(
          "Error while rejecting revision: " + errorMessage,
          "error"
        );
      }
      setIsRejectDisabled(false);
    }
  };

  if (!isOpen) return null;

  const currentRev = revisionData?.find(
    (r) => r.procedure_revision_id === revisionId
  );

  return (
    <>
      <div
        className="modal-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <div
          className="modal-content1"
          style={{
            position: "relative",
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "20px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            overflowY: "auto",
            overflowX: "hidden",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <h3
              className="modal-header"
              style={{ fontSize: "14px", margin: 0 }}
            >
              Reject Revision from Release
            </h3>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} size="1x" color="gray" />
            </button>
          </div>
          <hr className="divider" />

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              columnGap: "12px",
              rowGap: "4px",
              marginLeft: "10px",
              marginBottom: "4px",
            }}
          >
            <strong style={{ fontSize: "15px" }}>
              {type} {partNumber}
            </strong>
            <RenderHtml
              content={currentRev?.part_description}
              inline
            />
          </div>

          <div className="form-group">
            <label style={{ fontSize: "13px", fontWeight: "bold" }}>
              Revision: {myRevision}
            </label>
          </div>

          <div className="form-group">
            <label>
              <span style={{ fontSize: "13px", fontWeight: "bold" }}>
                Rejector:
              </span>{" "}
              {localStorage.getItem("user_name")}
            </label>
          </div>

          <div className="form-group">
            <label style={{ fontSize: "13px", fontWeight: "bold" }}>
              Comment<span className="required">*</span>
            </label>
            <textarea
              className="input-textarea"
              placeholder="Enter comment..."
              value={revisionDetails}
              onChange={(e) => setRevisionDetails(e.target.value)}
              style={{ minHeight: "70px" }}
            />
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              marginTop: "-17px",
            }}
          >
            <input
              type="checkbox"
              style={{ width: "13px", height: "13px", marginLeft: "10px" }}
              onChange={() => setIsReviewed(!isReviewed)}
              checked={isReviewed}
            />
            <h3
              style={{
                fontSize: "13px",
                fontWeight: "bold",
                marginLeft: "-5px",
              }}
            >
              I have reviewed the revisions mentioned on previous screen
              <span style={{ color: "#0D6EFD", marginLeft: "-1px" }}>*</span>
            </h3>
          </label>

          {/* Password - Only show if password is in auth methods */}
          {authMethods.includes("password") && (
            <div className="form-group" style={{ marginTop: "-12px" }}>
              <label style={{ fontSize: "13px", fontWeight: "bold" }}>
                Enter Password<span className="required">*</span>
              </label>
              <input
                type="password"
                className="input-field"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}

          {/* Signature - Only show if signature is in auth methods */}
          {authMethods.includes("signature") && (
            <SignatureField
              ref={sigPadRef}
              label="Sign Here"
              required={true}
              width="480px"
              height="100px"
              onEnd={handleEndDrawing}
              onClear={handleClearSignature}
            />
          )}

          <ModalActions
            onClose={onClose}
            onSubmit={handleRejectRevision}
            submitLabel="Reject"
            submitVariant="danger"
            loading={isRejectDisabled}
            loadingText="Rejecting..."
          />
        </div>
      </div>

      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </>
  );
};

export default RejectRevisionModal;
